import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled, Theme, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Grid } from '@mui/system';
import { ApiService } from 'api';
import {
  boldTextFooter,
  CREATE_OR_SEND_REQUESTS,
  MODAL_ACTION_BUTTON,
  MODAL_TITLE,
  SYNC_COMPANY_TEMPLATES,
  TEXT_FOOTER,
} from 'common/constants/firms';
import { useCompanyCategoriesProcessManagement } from 'common/hooks';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { FormDialog } from 'components';
import CustomPrimaryButton from 'components/CustomPrimaryButton/CustomPrimaryButton';
import { manageApiRequest } from 'hooks/apiRequest';
import theme from 'theme';
import { FirmRequestsFooterProps } from './types';
import FRModalActionButton from '../FRModalActionButton/FRModalActionButton';
import FRModalContent from '../FRModalContent/FRModalContent';

const PREFIX = 'FirmRequestsFooter';

const classes = {
  root: `${PREFIX}-root`,
  progress: `${PREFIX}-progress`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.root}`]: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },

  [`& .${classes.progress}`]: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: '-12px',
    marginLeft: '-12px',
  },
}));

const FirmRequestsFooter: React.FC<FirmRequestsFooterProps> = ({ firmId, documents, questions, companies }) => {
  const [{ firmInfo, companyList, user }] = useStore() as unknown as UseStoreValues;
  const firmName = firmInfo ? firmInfo.name : '';
  const [open, setOpen] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const { companyCategories } = useCompanyCategoriesProcessManagement({ firmId, companies });
  const [sendToAllCompanies, setSendToAllCompanies] = useState(false);
  const [useCompanyTemplates, setUseCompanyTemplates] = useState(false);

  const [allCompaniesSelected, setAllCompaniesSelected] = useState(false);
  const {
    companiesWithMeasurementDateAndUsers,
    companiesWithMeasurementDateButNoUsers,
    companiesWithoutMeasurementDateButUsers,
    companiesWithoutMeasurementDateAndUsers,
    companiesWithRequests,
  } = companyCategories;

  useEffect(() => {
    if (!sendToAllCompanies) {
      setSelectedCompanies([]);
      setAllCompaniesSelected(false);
    }
  }, [sendToAllCompanies]);

  const [isSyncingComps, setIsSyncingComps] = useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const syncCompanyTemplates = useCallback(async () => {
    setIsSyncingComps(true);

    await manageApiRequest(
      Promise.all(
        companyList?.map(company =>
          company?.company_id !== undefined
            ? ApiService.apiCompaniesSyncCompanyQuestionsUpdate(company.company_id, {})
            : Promise.resolve()
        ) || []
      )
    );

    setIsSyncingComps(false);
  }, [companyList]);

  const FRModalContentProps = useMemo(
    () => ({
      companiesWithMeasurementDateAndUsers,
      companiesWithMeasurementDateButNoUsers,
      companiesWithoutMeasurementDateButUsers,
      companiesWithoutMeasurementDateAndUsers,
      selectedCompanies,
      setSelectedCompanies,
      sendToAllCompanies,
      setSendToAllCompanies,
      allCompaniesSelected,
      setAllCompaniesSelected,
      companiesWithRequests,
    }),
    [
      companiesWithMeasurementDateAndUsers,
      companiesWithMeasurementDateButNoUsers,
      companiesWithoutMeasurementDateButUsers,
      companiesWithoutMeasurementDateAndUsers,
      selectedCompanies,
      setSelectedCompanies,
      sendToAllCompanies,
      setSendToAllCompanies,
      allCompaniesSelected,
      setAllCompaniesSelected,
      companiesWithRequests,
    ]
  );

  const FRModalActionButtonProps = useMemo(
    () => ({
      selectedCompanies,
      setSelectedCompanies,
      handleClose,
      firmId,
      documents,
      questions,
      sendToAllCompanies,
      userId: user?.id,
      useCompanyTemplates,
      setUseCompanyTemplates,
    }),
    [
      user,
      useCompanyTemplates,
      setUseCompanyTemplates,
      selectedCompanies,
      setSelectedCompanies,
      handleClose,
      firmId,
      documents,
      questions,
      sendToAllCompanies,
    ]
  );

  return (
    <StyledGrid
      container
      spacing={2}
      style={{
        borderTop: `1px solid ${theme.palette.grey[300]}`,
        padding: '2rem 0',
      }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <Typography
          style={{
            fontSize: '1rem',
          }}>
          <b>{boldTextFooter}</b>
          {TEXT_FOOTER.substring(boldTextFooter.length)}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <div className={classes.root}>
          <CustomPrimaryButton onClick={handleClickOpen}>{CREATE_OR_SEND_REQUESTS}</CustomPrimaryButton>
          <CustomPrimaryButton onClick={syncCompanyTemplates} variant="outlined">
            {isSyncingComps ? (
              <span>
                <CircularProgress className={classes.progress} size={24} /> Syncing
              </span>
            ) : (
              <span>{SYNC_COMPANY_TEMPLATES}</span>
            )}
          </CustomPrimaryButton>
        </div>
        <FormDialog
          open={open}
          onClose={handleClose}
          title={MODAL_TITLE}
          subtitle={firmName}
          customButtonLabel={MODAL_ACTION_BUTTON}
          disableTitleClose={false}
          customButton={<FRModalActionButton {...FRModalActionButtonProps} />}
          showSaveButton={false}
          showCancelButton={false}>
          <FRModalContent {...FRModalContentProps} />
        </FormDialog>
      </Grid>
    </StyledGrid>
  );
};

export default FirmRequestsFooter;
